import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastNotify } from '@opswat/ui';
import { useIncidents } from 'services/api/Incidents';

const RESOLVED_STATUS = 'resolved';
const SESSION_KEY = 'incident-notified';

// Only allow http(s) links — guards against unsafe schemes (e.g. javascript:) from the API.
const isSafeHttpUrl = (url?: string): url is string => {
    if (!url) {
        return false;
    }
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
};

const hasSessionFlag = (): boolean =>
    typeof window !== 'undefined' && window.sessionStorage.getItem(SESSION_KEY) === '1';

const setSessionFlag = (): void => {
    if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(SESSION_KEY, '1');
    }
};

export const useIncidentNotification = (enabled = true) => {
    const { t } = useTranslation();
    const { toastNotify } = useToastNotify();
    const { data } = useIncidents();
    const hasNotified = useRef(false);

    useEffect(() => {
        if (!enabled || hasNotified.current || !data) {
            return;
        }

        // Persist across reloads/remounts within the session, not just the component lifetime.
        if (hasSessionFlag()) {
            hasNotified.current = true;
            return;
        }

        const incidents = Array.isArray(data.incidents) ? data.incidents : [];
        const activeIncidents = incidents.filter((inc) => inc.status?.toLowerCase() !== RESOLVED_STATUS);

        if (activeIncidents.length === 0) {
            return;
        }

        hasNotified.current = true;
        setSessionFlag();

        const firstIncident = activeIncidents[0];
        const message = firstIncident.name || t('There is an ongoing service incident');
        const statusUrl = firstIncident.shortlink || data.page?.url;

        toastNotify({
            title: t('Service Incident'),
            message,
            type: 'neutral',
            hasClose: true,
            duration: 0,
            ...(isSafeHttpUrl(statusUrl)
                ? {
                      link: {
                          label: t('View status page'),
                          url: statusUrl,
                      },
                  }
                : {}),
        });
    }, [enabled, data, t, toastNotify]);
};
